// BarChart.js
import moment from 'moment';
import * as d3 from 'd3';
import React, { useState, useEffect, useContext, useRef, useReducer } from 'react';
import { Switch, Spinner, HTMLSelect } from '@blueprintjs/core';
import { ContextMenu2, Popover2 } from '@blueprintjs/popover2';

import { ItemContext } from '../App';
import {
  PromaBlocks,
  PromaItemType,
  promaItemTypeTranslations,
  RiskItemType,
  ValueType
} from './itemDefinitions';
import getPromaHierachy from './getPromaHierarchy';
import { lightItemColor, PromaPopupContent } from './MapItems';
import HierarchyPopover from './HierarchyPopover';
import { itemReducer } from './itemReducers';
import { designBlockFromVal } from './DesignStatusItems';
import GeneralMultiSelect, { enumToArray } from '../views/GeneralMultiSelect';

const truncate = (text) => {
  let twidth = 430;
  text.each(function () {
    let text = d3.select(this);
    if (text.node().getComputedTextLength() < twidth) {
      return;
    }
    let letters = text.text().split('').reverse(),
      letter,
      line = [],
      x = text.attr('x'),
      dx = text.attr('dx'),
      y = text.attr('y'),
      dy = text.attr('dy'),
      tspan = text
        .text(null)
        .append('tspan')
        .attr('x', x)
        .attr('dx', dx)
        .attr('y', y)
        .attr('dy', dy);
    while ((letter = letters.pop())) {
      line.push(letter);
      tspan.text(line.join(''));
      if (tspan.node().getComputedTextLength() > twidth) {
        line.pop();
        line.push('...');
        tspan.text(line.join(''));
        return;
      }
    }
  });
};

const formatValue = d3
  .formatLocale({
    decimal: ',',
    thousands: '\u00a0',
    grouping: [3],
    currency: ['', '\u00a0€']
  })
  .format('$.3~s');

// const getAllianceColor = (nData) => {
//   if (!nData.Karaatti_factor) {return '#5C7080'}
//   const colorScheme = ["#002051","#002153","#002255","#002356","#002358","#002459","#00255a","#00255c","#00265d","#00275e","#00275f","#002860","#002961","#002962","#002a63","#002b64","#012b65","#022c65","#032d66","#042d67","#052e67","#052f68","#063069","#073069","#08316a","#09326a","#0b326a","#0c336b","#0d346b","#0e346b","#0f356c","#10366c","#12376c","#13376d","#14386d","#15396d","#17396d","#183a6d","#193b6d","#1a3b6d","#1c3c6e","#1d3d6e","#1e3e6e","#203e6e","#213f6e","#23406e","#24406e","#25416e","#27426e","#28436e","#29436e","#2b446e","#2c456e","#2e456e","#2f466e","#30476e","#32486e","#33486e","#34496e","#364a6e","#374a6e","#394b6e","#3a4c6e","#3b4d6e","#3d4d6e","#3e4e6e","#3f4f6e","#414f6e","#42506e","#43516d","#44526d","#46526d","#47536d","#48546d","#4a546d","#4b556d","#4c566d","#4d576d","#4e576e","#50586e","#51596e","#52596e","#535a6e","#545b6e","#565c6e","#575c6e","#585d6e","#595e6e","#5a5e6e","#5b5f6e","#5c606e","#5d616e","#5e616e","#60626e","#61636f","#62646f","#63646f","#64656f","#65666f","#66666f","#67676f","#686870","#696970","#6a6970","#6b6a70","#6c6b70","#6d6c70","#6d6c71","#6e6d71","#6f6e71","#706f71","#716f71","#727071","#737172","#747172","#757272","#767372","#767472","#777473","#787573","#797673","#7a7773","#7b7774","#7b7874","#7c7974","#7d7a74","#7e7a74","#7f7b75","#807c75","#807d75","#817d75","#827e75","#837f76","#848076","#858076","#858176","#868276","#878376","#888477","#898477","#898577","#8a8677","#8b8777","#8c8777","#8d8877","#8e8978","#8e8a78","#8f8a78","#908b78","#918c78","#928d78","#938e78","#938e78","#948f78","#959078","#969178","#979278","#989278","#999378","#9a9478","#9b9578","#9b9678","#9c9678","#9d9778","#9e9878","#9f9978","#a09a78","#a19a78","#a29b78","#a39c78","#a49d78","#a59e77","#a69e77","#a79f77","#a8a077","#a9a177","#aaa276","#aba376","#aca376","#ada476","#aea575","#afa675","#b0a775","#b2a874","#b3a874","#b4a974","#b5aa73","#b6ab73","#b7ac72","#b8ad72","#baae72","#bbae71","#bcaf71","#bdb070","#beb170","#bfb26f","#c1b36f","#c2b46e","#c3b56d","#c4b56d","#c5b66c","#c7b76c","#c8b86b","#c9b96a","#caba6a","#ccbb69","#cdbc68","#cebc68","#cfbd67","#d1be66","#d2bf66","#d3c065","#d4c164","#d6c263","#d7c363","#d8c462","#d9c561","#dbc660","#dcc660","#ddc75f","#dec85e","#e0c95d","#e1ca5c","#e2cb5c","#e3cc5b","#e4cd5a","#e6ce59","#e7cf58","#e8d058","#e9d157","#ead256","#ebd355","#ecd454","#edd453","#eed553","#f0d652","#f1d751","#f1d850","#f2d950","#f3da4f","#f4db4e","#f5dc4d","#f6dd4d","#f7de4c","#f8df4b","#f8e04b","#f9e14a","#fae249","#fae349","#fbe448","#fbe548","#fce647","#fce746","#fde846","#fde946","#fdea45"]
//   const number = Math.round(nData.Karaatti_factor * colorScheme.length) +1
//   return colorScheme[number]
// }

const getBlockAmountColor = (nData) => {
  if (nData.scope === 'some') {
    return '#0E5A8A';
  }
  if (nData.scope === 'all') {
    return '#5C7080';
  }
  if (nData.scope === 'single') {
    return '#137CBD';
  }
  return '#5C7080';
};

const PromaGantt = ({
  height,
  width,
  margin,
  data,
  inHeight,
  inWidth,
  dLinks,
  riippuvuudet,
  filterDict,
  setActiveNode,
  setMouseEvent,
  setTargetOffset
}) => {
  const ref = useRef();

  const getValue = (nData) => {
    let value;
    switch (filterDict.valueType) {
      case ValueType.MEDIAN:
        value = nData.calcMedian;
        break;
      case ValueType.P95:
        value = nData.calcP95;
        break;
      case ValueType.EVALUE:
        value = nData.calcEValue;
        break;
      default:
        console.log('wtf');
    }
    return value;
  };

  const getCircleSize = (nData) => Math.sqrt((Math.abs(nData.childCount) * 4) / Math.PI);

  const handleNodeClick = (e, d) => {
    setActiveNode(d);
    e.preventDefault();
    setMouseEvent(e);
    setTargetOffset({ left: e.clientX, top: e.clientY });
  };

  const draw = () => {
    d3.select(ref.current).selectAll('*').remove();

    const nodeSize = 17;
    // Draw L12 goals
    const tree = (data) => {
      let i = 0;
      return d3.hierarchy(data).eachBefore((d) => (d.index = i++));
    };
    const root = tree(data);
    const nodes = root.descendants();

    const svg = d3
      .select(ref.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', 'translate(30,0)');

    const timeRange = [new Date('2021-04-01'), new Date('2024-12-31')];

    const lineYears = [
      new Date('2020'),
      new Date('2021'),
      new Date('2022'),
      new Date('2023'),
      new Date('2024'),
      new Date('2025')
    ];

    const x = d3.scaleTime().domain(timeRange).range([600, inWidth]).nice();
    const y = d3
      .scaleLinear()
      .domain([inHeight * 0.9, 0])
      .range([inHeight, 0])
      .nice();

    const xAxis = (g, scale) =>
      g
        .attr('transform', `translate(0,${y(0)})`)
        .call(d3.axisBottom(scale).ticks(12))
        .style('font-size', 12);
    // .call((g) => g.select('.domain').attr('display', 'none'));

    // const yAxis = (g, scale) =>
    //   g
    //     .attr('transform', `translate(${x(0)},0)`)
    //     .call(d3.axisLeft(scale).ticks(12 * (height / width)))
    //     .call((g) => g.select('.domain').attr('display', 'none'));

    // center the action (handles multitouch)
    function center(event, target) {
      if (event.sourceEvent) {
        const p = d3.pointers(event, target);
        return [d3.mean(p, (d) => d[0]), d3.mean(p, (d) => d[1])];
      }
      return [width / 2, height / 2];
    }

    svg
      .append('rect')
      .attr('x', -10 * width)
      .attr('y', -50 * height)
      .attr('width', 20 * width)
      .attr('height', 100 * height)
      .attr('fill-opacity', '0');

    const rightNode = svg.append('g').selectAll('g').data(nodes).join('g');
    const title = rightNode.append('title').text((d) =>
      d
        .ancestors()
        .reverse()
        .map((d) => d.data.name)
        .join('/')
    );

    const gx = svg.append('g');
    const gy = svg.append('g');

    // z holds a copy of the previous transform, so we can track its changes
    let z = d3.zoomIdentity;

    // set up the ancillary zooms and an accessor for their transforms
    const zoomX = d3.zoom().scaleExtent([0.7, 10]);
    const zoomY = d3.zoom().scaleExtent([0.5, 1.5]);
    const tx = () => d3.zoomTransform(gx.node());
    const ty = () => d3.zoomTransform(gy.node());
    gx.call(zoomX).attr('pointer-events', 'none');
    gy.call(zoomY).attr('pointer-events', 'none');

    const backgroundRect = svg
      .append('rect')
      .attr('x', -30)
      .attr('y', 0)
      .attr('width', 630)
      .attr('height', height)
      .attr('fill', 'white');

    const leftNode = svg.append('g').selectAll('g').data(nodes).join('g');
    const leftTitle = leftNode.append('title').text((d) =>
      d
        .ancestors()
        .reverse()
        .map((d) => d.data.name)
        .join('/')
    );

    const format = d3.format(',');
    const formatTime = d3.timeFormat('%d.%m.%Y');

    let circles = svg.append('g');
    let text = svg.append('g');
    let link = svg.append('g');
    let rects = svg.append('g');
    let diamonds = svg.append('g');
    let yearLines = svg.append('g');
    let nowLine = svg.append('g');
    let goalListBackGround = svg.append('g');
    let goalChartBackGround = svg.append('g');
    let svgColumns = [];

    const redraw = () => {
      circles.remove();
      text.remove();
      link.remove();
      rects.remove();
      diamonds.remove();
      yearLines.remove();
      nowLine.remove();
      goalListBackGround.remove();
      goalChartBackGround.remove();

      svgColumns.map((column) => column.remove());
      svgColumns = [];
      const xr = tx().rescaleX(x);
      const yr = ty().rescaleY(y);
      gx.call(xAxis, xr);
      // gy.call(yAxis, yr);

      yearLines = rightNode
        .append('g')
        .attr('stroke', '#999')
        .selectAll('line')
        .data(lineYears)
        .join('line')
        .attr('x1', (d) => xr(d))
        .attr('x2', (d) => xr(d))
        .attr('y1', 0)
        .attr('y2', height);

      nowLine = rightNode
        .append('line')
        .attr('stroke', '#106BA3')
        .attr('x1', (d) => xr(new Date()))
        .attr('x2', (d) => xr(new Date()))
        .attr('y1', 0)
        .attr('y2', height);

      goalChartBackGround = rightNode.append('rect');

      goalChartBackGround
        .filter((d) => d.data.itemType === PromaItemType.FinalGoal)
        .attr('fill', '#D8E1E8')
        .attr('x', 600)
        .attr('width', width)
        .attr('y', (d) => yr(d.index * nodeSize) - 9)
        .attr('height', nodeSize);

      goalChartBackGround
        .filter((d) => d.data.itemType === PromaItemType.Milestone)
        .attr('fill', '#EBF1F5')
        .attr('x', 600)
        .attr('width', width)
        .attr('y', (d) => yr(d.index * nodeSize) - 9)
        .attr('height', nodeSize);

      rects = rightNode
        .append('rect')
        .attr('x', (d) =>
          d.data.startDate ? xr(moment(d.data.startDate)) : xr(moment(d.data.endDate)) - 5
        )
        .attr('y', (d) => yr(d.index * nodeSize) - 9)
        .attr('width', (d) => {
          if (d.data.startDate) {
            const rectWidth = xr(moment(d.data.endDate)) - xr(moment(d.data.startDate));
            if (rectWidth > 0) {
              return rectWidth;
            }
          }
        })
        .attr('height', nodeSize)
        .attr('fill', (d) => lightItemColor(d.data, filterDict.viewTime))
        .on('click', handleNodeClick)
        .style('cursor', 'pointer');

      diamonds = rightNode
        .filter(
          (d) =>
            !(d.data.startDate && xr(moment(d.data.endDate)) - xr(moment(d.data.startDate)) > 0)
        )
        .append('text')
        .attr('x', (d) =>
          d.data.startDate ? xr(moment(d.data.startDate)) : xr(moment(d.data.endDate)) - 5
        )
        .attr('y', (d) => yr(d.index * nodeSize))
        .attr('dy', '0.32em')
        .attr('fill', (d) => lightItemColor(d.data, filterDict.viewTime))
        .on('click', handleNodeClick)
        .style('cursor', 'pointer')
        .attr('font-weight', 900)
        .style('font-size', 20)
        .text('♦');

      // mainGoalLines = leftNode
      //   .append('line')
      //   .filter((d) => d.data.itemType === PromaItemType.FinalGoal)
      //   .attr('stroke', '#A7B6C2')
      //   .attr('x1', -30)
      //   .attr('x2', width)
      //   .attr('y1', (d) => yr(d.index * nodeSize) - 10.5)
      //   .attr('y2', (d) => yr(d.index * nodeSize) - 10.5);

      goalListBackGround = leftNode.append('rect');

      goalListBackGround
        .filter((d) => d.data.itemType === PromaItemType.FinalGoal)
        .attr('fill', '#D8E1E8')
        .attr('x', -30)
        .attr('width', 630)
        .attr('y', (d) => yr(d.index * nodeSize) - 9)
        .attr('height', nodeSize);

      goalListBackGround
        .filter((d) => d.data.itemType === PromaItemType.Milestone)
        .attr('fill', '#EBF1F5')
        .attr('x', -30)
        .attr('width', 630)
        .attr('y', (d) => yr(d.index * nodeSize) - 9)
        .attr('height', nodeSize);

      link = svg
        .append('g')
        .attr('fill', 'none')
        .attr('stroke', '#999')
        .selectAll('path')
        .data(root.links())
        .join('path')
        .attr(
          'd',
          (d) => `
        M${d.source.depth * nodeSize},${yr(d.source.index * nodeSize)}
        V${yr(d.target.index * nodeSize)}
        h${nodeSize}
      `
        );

      circles = leftNode
        .append('circle')
        .attr('cx', (d) => d.depth * nodeSize)
        .attr('cy', (d) => yr(d.index * nodeSize))
        .attr('r', 2.5)
        .attr('fill', (d) => lightItemColor(d.data, filterDict.viewTime))
        .on('click', handleNodeClick)
        .style('cursor', 'pointer')
        .attr('fill', (d) => (d.children ? null : '#999'));

      text = leftNode
        .append('text')
        .attr('dy', '0.32em')
        .attr('y', (d) => yr(d.index * nodeSize))
        .attr('x', (d) => d.depth * nodeSize + 6)
        .attr('fill', (d) => lightItemColor(d.data, filterDict.viewTime))
        .on('click', handleNodeClick)
        .style('cursor', 'pointer')
        .text((d) => `${d.data.name} (${d.data.block})`)
        .call(truncate);

      text.filter((d) => d.data.itemType === PromaItemType.FinalGoal).attr('font-weight', 700);
      text.filter((d) => d.data.itemType === PromaItemType.Milestone).attr('font-weight', 550);
      text.filter((d) => d.data.itemType === PromaItemType.Achievement).attr('font-weight', 300);
      text.filter((d) => d.data.itemType === PromaItemType.Task).attr('font-weight', 100);
      text.filter((d) => d.data.itemType === 'GoalGoal').attr('font-weight', 700);

      const columns = [
        // {
        //   label: '',
        //   value: (d) => (d.data.startDate ? moment(d.data.startDate) : ''),
        //   format: formatTime,
        //   x: 300
        // },
        // {
        //   label: '',
        //   value: (d) => '➔',
        //   format: (v) => v,
        //   x: 320
        // },
        {
          label: '',
          value: (d) => (d.data.endDate ? moment(d.data.endDate) : ''),
          format: formatTime,
          x: 375
        }
      ];

      for (const { label, value, format, x } of columns) {
        const colText = leftNode
          .append('text')
          .attr('dy', '0.32em')
          .attr('y', (d) => yr(d.index * nodeSize))
          .attr('x', 1.5 * x)
          .attr('text-anchor', 'end')
          .attr('font-weight', 'bold')
          .text(label);

        const colValue = leftNode
          .append('text')
          .attr('dy', '0.32em')
          .attr('dx', '1.5em')
          .attr('x', 1.5 * x)
          .attr('y', (d) => yr(d.index * nodeSize))
          .attr('text-anchor', 'end')
          .text((d) => (value(d) !== '' ? format(value(d)) : ''));

        svgColumns = svgColumns.concat([colText, colValue]);
      }
    };

    // active zooming
    const zoom = d3.zoom().on('zoom', function (e) {
      const t = e.transform;
      const k = t.k / z.k;
      const point = center(e, this);

      // is it on an axis? is the shift key pressed?
      const doX = point[0] > x.range()[0];
      const doY = point[1] < y.range()[0];
      // const clientX = e.sourceEvent && e.sourceEvent.clientX;

      if (k === 1) {
        // pure translation?
        doX && gx.call(zoomX.translateBy, (t.x - z.x) / tx().k, 0);
        doY && gy.call(zoomY.translateBy, 0, (t.y - z.y) / ty().k);
      } else {
        // if not, we're zooming on a fixed point
        doX && gx.call(zoomX.scaleBy, k, point);
        //doY && gy.call(zoomY.scaleBy, k, point);
      }

      z = t;

      redraw();
    });
    svg.call(zoom).call(zoom.transform, d3.zoomIdentity.scale(0.6)).node();
  };

  useEffect(() => {
    draw();
  }, [height, width, data, inHeight, inWidth, filterDict.valueType]);

  return (
    <div className="chart">
      <svg ref={ref} />
    </div>
  );
};

const GanttItems = ({ showItems, itemProps, filterDict }) => {
  const [height, setHeight] = useState(window.innerHeight - 190);
  const [width, setWidth] = useState(window.innerWidth);
  const [taskHierarchy, setTaskHierarchy] = useState();
  const [links, setLinks] = useState([]);
  const [hLevel, setHLevel] = useState(PromaItemType.Milestone);
  const [riippuvuudet, setRiippuvuudet] = useState(false);
  const [filterBlocks, dispatchFilterBlocks] = useReducer(itemReducer, [
    { id: 'L3B', itemType: 'Block', name: '3B' }
  ]);

  const { filteredItems, items } = useContext(ItemContext);

  const blockFilteredGoals = items
    .filter((item) => item.itemType === PromaItemType.FinalGoal)
    .filter((item) => {
      return (
        filterBlocks.length === 0 || filterBlocks.map((block) => block.name).includes(item.block)
      );
    });

  // Handle node clicks
  const [activeNode, setActiveNode] = useState();
  const [mouseEvent, setMouseEvent] = useState();
  const [targetOffset, setTargetOffset] = useState();

  const margin = { top: 50, right: 30, bottom: 0, left: 50 };
  const inWidth = width - margin.left - margin.right;
  const inHeight = height - margin.top - margin.bottom;

  /** Update the size of the graph element - with a short delay to avoid too many renders */
  const updateGraphSize = () => {
    setTimeout(() => {
      if (height !== window.innerHeight - 190) {
        setHeight(window.innerHeight - 190);
      }
      if (width !== window.innerWidth) {
        setWidth(window.innerWidth);
      }
    }, 200);
  };

  window.addEventListener('resize', updateGraphSize);

  let showItemLevelFound = false;
  const newShowItems = [];
  for (const showitem of showItems) {
    if (!showItemLevelFound) {
      newShowItems.push(showitem);
    }
    if (showitem.itemType === hLevel) {
      showItemLevelFound = true;
    }
  }

  useEffect(() => {
    getPromaHierachy({
      showItems: newShowItems,
      itemProps: { ...itemProps, [PromaItemType.FinalGoal]: { filteredItems: blockFilteredGoals } },
      setTaskHierarchy,
      setLinks
    });
  }, [itemProps, filterDict.kvPhase, filterDict.currentBlock, hLevel, filterBlocks]);

  const filtrableBlocks = enumToArray(PromaBlocks, PromaItemType.Block);

  const chartProps = {
    height,
    width,
    margin,
    inHeight,
    inWidth,
    filterDict,
    setActiveNode,
    setMouseEvent,
    setTargetOffset
  };

  if (!taskHierarchy) {
    return (
      <div>
        <Spinner className="centered" size={100} />
      </div>
    );
  }

  return (
    <div>
      <HierarchyPopover
        content={
          activeNode ? (
            <PromaPopupContent item={activeNode.data} filteredItems={filteredItems} hideChildren />
          ) : (
            ''
          )
        }
        node={activeNode}
        mouseEvent={mouseEvent}
        setMouseEvent={setMouseEvent}
        targetOffset={targetOffset}
      />
      {/* <Switch
        label="Näytä riippuvuudet"
        checked={riippuvuudet}
        onChange={() => {
          setRiippuvuudet(!riippuvuudet);
        }}
        inline
        alignIndicator="left"
      /> */}
      <HTMLSelect
        value={hLevel}
        onChange={(e) => setHLevel(e.currentTarget.value)}
        options={[
          { label: 'Päätavoitetaso', value: PromaItemType.FinalGoal },
          { label: 'Välitavoitetaso', value: PromaItemType.Milestone },
          { label: 'Tuotostaso', value: PromaItemType.Achievement },
          { label: 'Tehtävätaso', value: PromaItemType.Task }
        ]}
      />
      <GeneralMultiSelect
        key="Lohko"
        placeholderText="Lohko"
        filtrableItems={filtrableBlocks}
        filterItems={filterBlocks}
        dispatchFilterItems={dispatchFilterBlocks}
      />
      <PromaGantt {...chartProps} data={taskHierarchy} dLinks={links} riippuvuudet={riippuvuudet} />
      <p style={{ paddingLeft: '10px' }}>
        Värit: <span style={{ color: '#738694' }}>Ei aloitettu</span>
        {', '}
        <span style={{ color: '#137CBD' }}>Käynnissä</span>
        {', '}
        <span style={{ color: '#DB3737' }}>Myöhässä (vain nykyhetki)</span>{' '}
        <span style={{ color: '#15B371' }}>Valmis</span>{' '}
      </p>
    </div>
  );
};

export default GanttItems;
